import React, { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Radio, Trash2, Pencil, X, Save, FilePlus, GraduationCap, AlertCircle, MessageCircle, Send } from "lucide-react";

const empty = { title: "", description: "", category: "Entry Level", price: 0, cover_url: "", is_live: false };
const emptyLesson = () => ({ title: "", content: "", video_url: "", duration_min: 10 });

export default function TeachPage() {
  useSeo({ title: "Faculty portal", path: "/teach", noindex: true });
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(empty);
  const [lessons, setLessons] = useState([emptyLesson()]);
  const [quiz, setQuiz] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = create mode, string = edit mode
  const [showLive, setShowLive] = useState(null);
  const [liveForm, setLiveForm] = useState({ title: "", scheduled_at: "", duration_min: 60, meeting_url: "" });
  const [attempts, setAttempts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [scorebook, setScorebook] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/instructor/courses").then((r) => setCourses(r.data));
  const loadAttempts = () => api.get("/teach/exam-attempts").then((r) => setAttempts(r.data)).catch(() => {});
  const loadEnquiries = () => api.get("/teach/enquiries").then((r) => setEnquiries(r.data || [])).catch(() => {});
  const loadScorebook = () => api.get("/teach/scorebook").then((r) => setScorebook(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadAttempts(); loadEnquiries(); loadScorebook(); }, []);

  const sendReply = async (e) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/enquiries/${e.id}/reply`, { text: replyText.trim() });
      toast.success("Reply sent");
      setReplyFor(null); setReplyText("");
      loadEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reply failed"); }
  };

  const resetForm = () => {
    setForm(empty);
    setLessons([emptyLesson()]);
    setQuiz([]);
    setEditingId(null);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title || "",
      description: c.description || "",
      category: c.category || "Entry Level",
      price: c.price ?? 0,
      cover_url: c.cover_url || "",
      is_live: !!c.is_live,
    });
    setLessons((c.lessons && c.lessons.length ? c.lessons : [emptyLesson()]).map((l) => ({
      id: l.id,
      title: l.title || "",
      content: l.content || "",
      video_url: l.video_url || "",
      duration_min: l.duration_min ?? 10,
    })));
    setQuiz((c.quiz || []).map((q) => ({ q: q.q || "", options: q.options || ["", "", "", ""], answer_index: q.answer_index ?? 0 })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price), lessons, quiz };
    try {
      if (editingId) {
        await api.put(`/courses/${editingId}`, payload);
        toast.success("Course updated");
      } else {
        await api.post("/courses", payload);
        toast.success("Course published");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This also removes all enrolments, live sessions, and comments. This cannot be undone.`)) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success("Course deleted");
      if (editingId === c.id) resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const scheduleLive = async (e) => {
    e.preventDefault();
    try {
      await api.post("/live-sessions", {
        course_id: showLive,
        title: liveForm.title,
        scheduled_at: new Date(liveForm.scheduled_at).toISOString(),
        duration_min: Number(liveForm.duration_min),
        meeting_url: liveForm.meeting_url,
      });
      toast.success("Live session scheduled");
      setShowLive(null);
      setLiveForm({ title: "", scheduled_at: "", duration_min: 60, meeting_url: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const isEditing = !!editingId;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="eyebrow mb-2">Instructor studio</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-8">
        {isEditing ? "Edit your course." : "Design a course that feels alive."}
      </h1>

      {scorebook.length > 0 && (
        <section className="mb-8" data-testid="teach-scorebook">
          <div className="eyebrow inline-flex items-center gap-1 text-[var(--gold-deep)]"><GraduationCap size={13}/> My cohort scorebook</div>
          <h2 className="font-display text-2xl mt-1 mb-3">How your courses are performing</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {scorebook.map((c) => (
              <div key={c.id} className="card-warm p-4" data-testid={`scorebook-${c.code}`}>
                <div className="eyebrow">{c.code}</div>
                <div className="font-display text-base leading-tight mb-2">{c.title}</div>
                {!c.has_exam ? (
                  <div className="text-xs text-zinc-500">No exam set up yet.</div>
                ) : c.total_attempts === 0 ? (
                  <div className="text-xs text-zinc-500">No candidates have sat {c.exam_title} yet.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      <StatMini label="Candidates" value={c.total_attempts}/>
                      <StatMini label="Pass rate" value={c.pass_rate != null ? `${c.pass_rate}%` : "-"} tone={c.pass_rate >= 65 ? "good" : c.pass_rate < 50 ? "bad" : "neutral"}/>
                      <StatMini label="Average" value={c.mean_percent != null ? `${c.mean_percent}%` : "-"}/>
                      <StatMini label="Awaiting grade" value={c.awaiting_grade} tone={c.awaiting_grade > 0 ? "warn" : "good"} testId={`awaiting-${c.code}`}/>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-2">
                      {c.pass_count} pass · {c.fail_count} fail · {c.graded_count}/{c.total_attempts} graded
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {attempts.length > 0 && (
        <section className="mb-8 card-warm p-5" data-testid="pending-grading-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="eyebrow inline-flex items-center gap-1 text-[var(--terracotta)]"><GraduationCap size={13}/> Essays awaiting your grade</div>
              <h2 className="font-display text-2xl mt-1">{attempts.filter((a) => a.status === "submitted").length} submission{attempts.filter((a) => a.status === "submitted").length === 1 ? "" : "s"} pending</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left">
                <tr className="border-b border-[var(--border-warm)]">
                  <th className="py-2 pr-4">Candidate</th>
                  <th className="py-2 pr-4">Exam · Course</th>
                  <th className="py-2 pr-4">Submitted</th>
                  <th className="py-2 pr-4">Auto</th>
                  <th className="py-2 pr-4">Flags</th>
                  <th className="py-2 pr-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.id} className="border-b border-[var(--border-warm)]" data-testid={`teach-attempt-${a.id}`}>
                    <td className="py-2 pr-4 text-xs"><div className="font-semibold">{a.user_name}</div><div className="text-[10px] text-zinc-500">{a.user_email}</div></td>
                    <td className="py-2 pr-4 text-xs">{a.exam_title}<div className="text-[10px] text-zinc-500">{a.course_code}</div></td>
                    <td className="py-2 pr-4 text-xs">{(a.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 pr-4 text-xs font-mono">{a.auto_score}/{a.auto_total}</td>
                    <td className="py-2 pr-4 text-xs">
                      {(a.integrity_events || []).length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><AlertCircle size={12}/> {a.integrity_events.length}</span>
                      ) : <span className="text-zinc-400">-</span>}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <button onClick={() => nav(`/admin/exam-attempts/${a.id}`)} className="chip inline-flex items-center gap-1 text-xs" data-testid={`teach-grade-${a.id}`}>
                        <GraduationCap size={12}/> {a.status === "submitted" ? "Grade" : "Review"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {enquiries.length > 0 && (
        <section className="mb-8 card-warm p-5" data-testid="teach-enquiries-section">
          <div className="eyebrow inline-flex items-center gap-1 text-[var(--terracotta)]"><MessageCircle size={13}/> Candidate enquiries on your courses</div>
          <h2 className="font-display text-2xl mt-1 mb-3">{enquiries.filter((e) => (e.messages || []).slice(-1)[0]?.role === "student").length} awaiting your reply</h2>
          <div className="space-y-3">
            {enquiries.map((e) => {
              const last = (e.messages || []).slice(-1)[0];
              const pending = last && last.role === "student";
              return (
                <div key={e.id} className="rounded-xl border border-[var(--border-warm)] p-4" data-testid={`teach-enquiry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="eyebrow inline-flex items-center gap-2">
                        {e.course_title}
                        {pending && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">awaiting reply</span>}
                      </div>
                      <h3 className="font-display text-lg">{e.subject}</h3>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        From <b>{e.user_name}</b> &lt;{e.user_email}&gt; · {e.messages?.length || 0} message{(e.messages?.length || 0) === 1 ? "" : "s"} · updated {new Date(e.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${e.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{e.status}</span>
                  </div>
                  <ul className="mt-3 space-y-2 max-h-80 overflow-y-auto">
                    {(e.messages || []).map((m) => (
                      <li key={m.id} className={`rounded-xl p-3 text-sm ${m.role === "student" ? "bg-[var(--muted-surface)]" : m.role === "instructor" ? "bg-[var(--navy)]/5 border-l-2 border-[var(--gold-deep)]" : "bg-[var(--navy)]/5 border-l-2 border-[var(--terracotta)]"}`}>
                        <div className="text-[11px] text-zinc-500 mb-1"><b>{m.by_name}</b> · {m.role} · {new Date(m.at).toLocaleString()}</div>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </li>
                    ))}
                  </ul>
                  {replyFor === e.id ? (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <textarea
                        value={replyText}
                        onChange={(ev) => setReplyText(ev.target.value)}
                        placeholder="Reply to the candidate…"
                        className="flex-1 min-w-[240px] p-2 border border-[var(--border-warm)] rounded-lg text-sm"
                        rows={3}
                        autoFocus
                        data-testid={`teach-enquiry-reply-input-${e.id}`}
                      />
                      <div className="flex flex-col gap-2">
                        <button onClick={() => sendReply(e)} className="btn-terracotta inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm" data-testid={`teach-enquiry-send-${e.id}`}><Send size={13}/> Send</button>
                        <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="chip text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setReplyFor(e.id); setReplyText(""); }} className="chip mt-3 text-xs inline-flex items-center gap-1" data-testid={`teach-enquiry-reply-${e.id}`}>
                      <Send size={12}/> Reply
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="grid lg:grid-cols-12 gap-6">
        <form onSubmit={submit} className="lg:col-span-7 card-warm p-6 space-y-4" data-testid="course-form">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display text-2xl inline-flex items-center gap-2">
              {isEditing ? <><Pencil size={18} className="text-[var(--terracotta)]"/> Editing course</> : <><FilePlus size={18} className="text-[var(--terracotta)]"/> New course</>}
            </h2>
            {isEditing && (
              <button type="button" onClick={resetForm} className="chip inline-flex items-center gap-1 text-xs" data-testid="cancel-edit-btn">
                <X size={13}/> Cancel edit
              </button>
            )}
          </div>

          <div className="grid gap-3">
            <input className="field" placeholder="Course title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="course-title-input"/>
            <textarea className="field" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="course-desc-input"/>
            <div className="grid grid-cols-2 gap-3">
              <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="course-cat-select">
                {["Entry Level","Professional","Leadership","Executive","Postgraduate"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="field" type="number" step="0.01" placeholder="Price EUR (0 = free)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="course-price-input"/>
            </div>
            <input className="field" placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} data-testid="course-cover-input"/>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })} className="accent-[var(--terracotta)]" data-testid="course-is-live"/>
              This course has live cohort sessions
            </label>
          </div>

          <div>
            <div className="eyebrow mb-2">Lessons</div>
            <div className="space-y-3">
              {lessons.map((l, i) => (
                <div key={i} className="border border-[var(--border-warm)] rounded-xl p-3" data-testid={`lesson-row-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input className="field flex-1" placeholder={`Lesson ${i + 1} title`} value={l.title} onChange={(e) => update(lessons, setLessons, i, { title: e.target.value })} data-testid={`lesson-title-${i}`}/>
                    <input className="field w-28" type="number" placeholder="Min" value={l.duration_min} onChange={(e) => update(lessons, setLessons, i, { duration_min: Number(e.target.value) })}/>
                    <button type="button" className="chip" onClick={() => setLessons(lessons.filter((_, j) => j !== i))} data-testid={`lesson-delete-${i}`}><Trash2 size={14}/></button>
                  </div>
                  <input className="field mb-2" placeholder="YouTube embed URL (optional)" value={l.video_url} onChange={(e) => update(lessons, setLessons, i, { video_url: e.target.value })}/>
                  <textarea className="field" placeholder="Lesson content" value={l.content} onChange={(e) => update(lessons, setLessons, i, { content: e.target.value })}/>
                </div>
              ))}
              <button type="button" className="chip inline-flex items-center gap-1" onClick={() => setLessons([...lessons, emptyLesson()])} data-testid="add-lesson-btn">
                <Plus size={14}/> Add lesson
              </button>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2">Quiz (optional)</div>
            <div className="space-y-3">
              {quiz.map((q, i) => (
                <div key={i} className="border border-[var(--border-warm)] rounded-xl p-3" data-testid={`quiz-row-${i}`}>
                  <input className="field mb-2" placeholder="Question" value={q.q} onChange={(e) => update(quiz, setQuiz, i, { q: e.target.value })}/>
                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => (
                      <input key={oi} className="field" placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => {
                        const ns = [...q.options]; ns[oi] = e.target.value;
                        update(quiz, setQuiz, i, { options: ns });
                      }}/>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs">Correct answer index:</label>
                    <input className="field w-20" type="number" min="0" max="3" value={q.answer_index} onChange={(e) => update(quiz, setQuiz, i, { answer_index: Number(e.target.value) })}/>
                    <button type="button" className="chip ml-auto" onClick={() => setQuiz(quiz.filter((_, j) => j !== i))}><Trash2 size={14}/></button>
                  </div>
                </div>
              ))}
              <button type="button" className="chip inline-flex items-center gap-1" onClick={() => setQuiz([...quiz, { q: "", options: ["","","",""], answer_index: 0 }])} data-testid="add-quiz-btn">
                <Plus size={14}/> Add question
              </button>
            </div>
          </div>

          <button
            className="btn-terracotta w-full py-3 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2"
            data-testid={isEditing ? "save-course-btn" : "publish-course-btn"}
          >
            {isEditing ? <><Save size={14}/> Save changes</> : <><FilePlus size={14}/> Publish course</>}
          </button>
        </form>

        <aside className="lg:col-span-5 card-warm p-6">
          <h2 className="font-display text-2xl mb-4">Your courses</h2>
          {courses.length === 0 ? (
            <div className="text-sm text-zinc-500">You haven't published a course yet.</div>
          ) : (
            <div className="space-y-3">
              {courses.map((c) => (
                <div
                  key={c.id}
                  className={`border rounded-xl p-3 ${editingId === c.id ? "border-[var(--terracotta)] bg-[var(--muted-surface)]/60" : "border-[var(--border-warm)]"}`}
                  data-testid={`mycourse-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{c.title}</div>
                      <div className="text-xs text-zinc-500">
                        {c.enrolled_count || 0} enrolled · €{c.price} · {(c.lessons || []).length} lessons
                      </div>
                    </div>
                    {editingId === c.id && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-[var(--terracotta)] text-white px-2 py-0.5 rounded-full">Editing</span>
                    )}
                  </div>
                  <div className="mt-2.5 flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="chip inline-flex items-center gap-1 text-xs"
                      onClick={() => startEdit(c)}
                      disabled={editingId === c.id}
                      data-testid={`edit-course-${c.id}`}
                    >
                      <Pencil size={12}/> Edit
                    </button>
                    <button
                      type="button"
                      className="chip inline-flex items-center gap-1 text-xs"
                      onClick={() => setShowLive(c.id)}
                      data-testid={`schedule-live-${c.id}`}
                    >
                      <Radio size={12}/> Schedule live
                    </button>
                    <button
                      type="button"
                      className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)] hover:bg-red-50 ml-auto"
                      onClick={() => removeCourse(c)}
                      data-testid={`delete-course-${c.id}`}
                    >
                      <Trash2 size={12}/> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </section>

      {showLive && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setShowLive(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={scheduleLive} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
            <h3 className="font-display text-2xl">Schedule live session</h3>
            <input className="field" placeholder="Session title" value={liveForm.title} onChange={(e) => setLiveForm({ ...liveForm, title: e.target.value })} required data-testid="live-title-input"/>
            <input className="field" type="datetime-local" value={liveForm.scheduled_at} onChange={(e) => setLiveForm({ ...liveForm, scheduled_at: e.target.value })} required data-testid="live-datetime-input"/>
            <input className="field" type="number" placeholder="Duration (min)" value={liveForm.duration_min} onChange={(e) => setLiveForm({ ...liveForm, duration_min: e.target.value })}/>
            <input className="field" placeholder="Meeting URL (leave blank for auto)" value={liveForm.meeting_url} onChange={(e) => setLiveForm({ ...liveForm, meeting_url: e.target.value })}/>
            <div className="flex gap-2 pt-2">
              <button type="button" className="chip flex-1" onClick={() => setShowLive(null)}>Cancel</button>
              <button className="btn-terracotta flex-1 py-2 rounded-full text-sm font-semibold" data-testid="schedule-live-submit">Schedule</button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .field { width: 100%; background: #fff; border: 1px solid var(--border-warm); border-radius: 12px; padding: 10px 14px; font-size: 14px; outline: none; font-family: inherit; }
        .field:focus { border-color: var(--ink); }
        textarea.field { min-height: 70px; resize: vertical; }
      `}</style>
    </div>
  );
}

function StatMini({ label, value, tone = "neutral", testId }) {
  const toneCls = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-700" : tone === "warn" ? "text-amber-700" : "text-[var(--navy)]";
  return (
    <div className="rounded-lg bg-[var(--muted-surface)] p-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-display text-xl ${toneCls}`}>{value}</div>
    </div>
  );
}


function update(arr, setter, idx, patch) {
  const n = arr.map((item, i) => (i === idx ? { ...item, ...patch } : item));
  setter(n);
}
